// The visibility modes a persona can view a grid through, and how they group.
//
// A user has exactly one persona active at a time, and that persona belongs to one FAMILY -- a
// Position, a User Group or a Work Group. Only the modes of the active persona's family are offered:
// a User Group persona has no "Manager" reading, so showing that tab would present a selection that
// cannot resolve. Callers filter with familyOfMode(activeVisibility.Mode).
//
// Ported from the Data Catalog's copy so the two apps cannot drift apart on the mode list or the
// family mapping. The backend reads the chosen mode from the visibility-mode / visibility-value
// headers, which PlatformTable sets on the EC from the active visibility.
export const VISIBILITY_MODES: {mode: string; label: string; family: string}[] = [
    // THE ROLLUP, ABOVE ORGANIZATION because it is the wider of the two: your organization plus
    // the organizations nested under it. Inert in a tenant with one organization, which is why it
    // sits behind its own grant rather than being folded into persona:organization.
    {mode: "Sub-Organization", label: "Sub-Organization", family: "position"},
    {mode: "Organization", label: "Organization", family: "position"},
    {mode: "Management", label: "Manager", family: "position"},
    {mode: "Position", label: "Position", family: "position"},
    {mode: "User Group", label: "User Group", family: "usergroup"},
    {mode: "Work Group", label: "Work Group", family: "workgroup"},
];

// The login form spells these without spaces; the NavBar Personas menu spells them with. Both reach
// here, so both are accepted. Anything unrecognised falls back to the position family, which is the
// default persona kind.
export function familyOfMode(mode: string): string {
    if (mode === "User Group" || mode === "UserGroup") return "usergroup";
    if (mode === "Work Group" || mode === "WorkGroup") return "workgroup";
    return "position";
}

// The modes that read BEYOND the active position, and the privilege each one requires.
//
// Position is absent and must stay absent: it needs no grant, and treating it like these would hide a
// user's own data from them whenever a tenant had not been provisioned.
const WIDENING_PRIVILEGE: Record<string, string> = {
    "Sub-Organization": "persona:suborganization",
    "Organization": "persona:organization",
    "Management": "persona:manager",
};

// The modes to offer: the active persona's family, minus any widened reading the caller has not been
// granted.
//
// HIDING IS UX, NOT ENFORCEMENT. `may_assume` refuses an ungranted mode whatever the client sends, so
// this changes nothing about what can be read — it changes whether the user is shown a tab that would
// answer 403. A control that fails when you press it reads as a broken product rather than as a
// permission you were never given.
//
// Privileges arrive from login as CODES (`persona:manager`), which is what guards match on; an absent
// or empty list simply offers the position itself.
//
// Design: cres-t2/docs/access-model/visibility-modes.md
export function availableModes(
    activeMode: string,
    privileges: string[] | undefined | null,
    account?: {Positions?: any[]; UserGroups?: any[]; WorkGroups?: any[]} | null,
) {
    const family = familyOfMode(activeMode);
    const held = privileges ?? [];
    const modes = VISIBILITY_MODES
        .filter(mode => mode.family === family)
        .filter(mode => !(mode.mode in WIDENING_PRIVILEGE)
                        || held.includes(WIDENING_PRIVILEGE[mode.mode]));

    // A LONE TAB OFFERS NO CHOICE. One position and no widening grants leaves "Position" sitting
    // there by itself, describing the only view its owner will ever get — a control that cannot be
    // used, which reads as something being broken rather than as nothing to decide.
    //
    // The count is what makes this safe to apply to the tab rather than to the grant. Somebody with
    // SEVERAL positions keeps the lone tab, because it tells them which of them they are currently
    // looking through even though the tab row itself offers nothing to press. And anybody who can
    // widen keeps Position as the way back to their own records, since the row is no longer lone.
    if (modes.length < 2 && personaCount(family, account) <= 1) {
        return [];
    }

    return modes;
}

function personaCount(
    family: string,
    account?: {Positions?: any[]; UserGroups?: any[]; WorkGroups?: any[]} | null,
): number {
    if (!account) {
        // Unknown is treated as ONE, so an account that has not loaded hides a tab rather than
        // showing one that may turn out to be lone. The row appears a moment later either way.
        return 1;
    }
    if (family === "usergroup") return (account.UserGroups ?? []).length;
    if (family === "workgroup") return (account.WorkGroups ?? []).length;
    return (account.Positions ?? []).length;
}
